import {
	AfterViewInit,
	Component,
	ElementRef,
	OnDestroy,
	OnInit,
	Renderer2,
} from '@angular/core';
import { ActivatedRoute, Params, Router, RouterLink } from '@angular/router';
import { AsyncPipe, NgClass } from '@angular/common';
import { Store } from '@ngrx/store';
import { combineLatest, delay, Subscription, tap } from 'rxjs';
import { AdviceService } from '../../services/advice.service';
import {
	getStateAdviceAllArticles,
	getStateAdviceFeatured,
} from '../../data-access/+state/advice.selectors';
import {
	fetchAllArticles,
	fetchFeaturedWithCategory,
} from '../../data-access/+state/advice.actions';
import { ArticleDetails } from '../../models/advice.models';
import { LoadingAnimationComponent, LoadingAnimationService } from '@uc/shared/ui';
import { SnigelService } from '@uc/web/core';
import { SeoMetadataService } from '@uc/utilities';
import { MainHeaderComponent, TableSearchComponent } from '@web/shared/ui';
import { ArrowLeftSvgComponent } from '@uc/shared/svg';
import {
	PromoSlotCtaCardComponent,
	PromoSlotsComponent,
} from '@uc/web/shared/feature/feature-promo-slots';
import { StickyLeadBarRendererComponent } from '@web/shared/feature/feature-sticky-lead-bar';
import { AdviceFeaturedCardComponent } from '../../components/advice-featured-card/advice-featured-card.component';
import { AdviceArticleCardComponent } from '../../components/advice-article-card/advice-article-card.component';
import { AdviceCategoryCardComponent } from '../../components/advice-category-card/advice-category-card.component';

@Component({
	selector: 'uc-advice-category',
	templateUrl: './advice-category.component.html',
	imports: [
		NgClass,
		AsyncPipe,
		RouterLink,
		MainHeaderComponent,
		ArrowLeftSvgComponent,
		LoadingAnimationComponent,
		AdviceFeaturedCardComponent,
		AdviceArticleCardComponent,
		AdviceCategoryCardComponent,
		PromoSlotsComponent,
		StickyLeadBarRendererComponent,
		TableSearchComponent,
		PromoSlotCtaCardComponent,
	],
})
export class AdviceCategoryComponent implements OnInit, OnDestroy, AfterViewInit {
	loadingHtml!: string;
	loadingForData = true;
	showReadMoreText = false;
	readMoreElement!: ElementRef;
	categoryId!: number;
	categoryDetails: any;
	relatedCategories: ArticleDetails[] = [];
	articles!: any;
	featured!: any;
	viewReady = false; //needed for NG checked error

	private _categoryUrl!: string;
	private _routerSub!: Subscription;

	articles$ = combineLatest([
		this._store.select(getStateAdviceAllArticles),
		this._store.select(getStateAdviceFeatured),
	]).pipe(
		delay(1),
		tap((data) => {
			const [all, featured] = data;
			this.articles = all;
			this.featured = featured;
			this.loadingForData = !all && !featured;

			if (!this.loadingForData) {
				this._snigelSrv.getAds(['incontent_1_dskt', 'incontent_1_mob']);
				this.viewReady = true;

				// resetting read more button state and element on route navigation
				this.showReadMoreText = false;
				if (this.readMoreElement) {
					this.readMoreElement =
						this._elem.nativeElement.querySelector('.articles-about');
				}
			}
		}),
	);

	constructor(
		private _route: ActivatedRoute,
		private _router: Router,
		private _store: Store,
		private _renderer: Renderer2,
		private _elem: ElementRef,
		private _adviceSrv: AdviceService,
		private _seoSrv: SeoMetadataService,
		private _loadingSrv: LoadingAnimationService,
		private _snigelSrv: SnigelService,
	) {}

	ngOnInit(): void {
		this.loadingHtml = this._loadingSrv.createPara(2, 3);

		this._routerSub = this._route.params.subscribe((params: Params) => {
			this._categoryUrl = params['articleCategory'];

			//added back in the day - requested by George
			if (!this._adviceSrv.redirectFromIncorrectUrl(this._categoryUrl)) return;

			this.categoryDetails = this._adviceSrv.getCategoryDetails(this._categoryUrl);

			//no category found, return to general page
			if (this.categoryDetails.error) {
				this._router.navigate(['/advice']);
			}

			this.relatedCategories = [];
			this.categoryDetails.relatedCategories.forEach((relatedCategory: string) => {
				const categoryDetails =
					this._adviceSrv.getCategoryDetails(relatedCategory);
				this.relatedCategories.push(categoryDetails);
			});

			//seo
			this._seoSrv.updateTitle(this.categoryDetails.seoTitle);
			this._seoSrv.updateDescription(this.categoryDetails.seoDescription);

			//store
			const key = this.generateKey(this.categoryDetails.key);
			this._store.dispatch(fetchFeaturedWithCategory(key));
			this._store.dispatch(fetchAllArticles());
		});
	}

	ngAfterViewInit(): void {
		this.readMoreElement = this._elem.nativeElement.querySelector('.articles-about');
	}

	ngOnDestroy(): void {
		this._routerSub?.unsubscribe();
	}

	showMoreText() {
		if (this.showReadMoreText) {
			this._renderer.addClass(this.readMoreElement, 'hidden');
			this.showReadMoreText = false;
		} else {
			this._renderer.removeClass(this.readMoreElement, 'hidden');
			this.showReadMoreText = true;
		}
	}

	generateKey(key: string) {
		return key.toLowerCase().split('_').join(' ');
	}
}
